<template>
	<button
		v-tooltip="$t('time_entry_done')"
		type="button"
		class="time-entry-actions-tracking-button"
		:class="classAddons"
		:disabled="!canCompleteTracking"
		:aria-label="$t('time_entry_done')"
		@click="emit('complete')"
	>
		<span
			v-if="!trackingSuccessFlash"
			class="time-entry-actions-tracking-button__dot"
			aria-hidden="true"
		/>
		<span
			v-else
			class="time-entry-actions-tracking-button__icon-wrap"
			aria-hidden="true"
		>
			<SuiIcon
				class="time-entry-actions-tracking-button__success-icon"
				name="icon-check-16"
			/>
		</span>
		<span
			v-if="showTrackingDuration"
			class="time-entry-actions-tracking-button__time"
		>
			<span class="time-entry-actions-tracking-button__time-part">{{
				trackingTimePrimary
			}}</span>
			<span
				v-if="trackingTimeSecondary"
				class="time-entry-actions-tracking-button__time-part"
			>
				{{ trackingTimeSecondary }}
			</span>
		</span>
	</button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { SuiIcon } from '@developers/scoro-ui';

type TimeEntryActionsTrackingButtonProps = {
	trackingSuccessFlash: boolean;
	trackingSuccessLeaving: boolean;
	canCompleteTracking: boolean;
	isWaitingForActiveTimerId: boolean;
	showTrackingDuration: boolean;
	trackingTimePrimary: string;
	trackingTimeSecondary: string | null;
	trackingPausedForModal: boolean;
};

const {
	trackingSuccessFlash,
	trackingSuccessLeaving,
	isWaitingForActiveTimerId,
	trackingPausedForModal,
} = defineProps<TimeEntryActionsTrackingButtonProps>();

const emit = defineEmits<{
	(e: 'complete'): void;
}>();

const classAddons = computed(() => ({
	'time-entry-actions-tracking-button--success': trackingSuccessFlash,
	'time-entry-actions-tracking-button--success-leaving':
		trackingSuccessLeaving,
	'time-entry-actions-tracking-button--waiting-id': isWaitingForActiveTimerId,
	'time-entry-actions-tracking-button--modal-pause': trackingPausedForModal,
}));
</script>

<style lang="scss">
.time-entry-actions-tracking-button {
	$parent: &;

	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 4rem;
	padding: 0 1rem;
	border: 1px solid transparent;
	border-radius: 0.5rem;
	background-color: $neutral-10;
	gap: 0.5rem;
	cursor: pointer;
	transition:
		background-color 0.2s ease,
		border-color 0.2s ease,
		color 0.2s ease;

	&:hover:not(:disabled, #{$parent}--success) {
		background-color: $neutral-20;
	}

	&:disabled {
		cursor: wait;
	}

	&--waiting-id:not(:disabled) {
		cursor: wait;
	}

	&--modal-pause:disabled {
		cursor: default;
	}

	&--success {
		border-color: $green-30;
		background-color: $green-10;
		cursor: default;
		opacity: 1;
		transition:
			background-color 0.2s ease,
			border-color 0.2s ease,
			color 0.2s ease,
			opacity 0.3s ease;

		&:hover {
			border-color: $green-30;
			background-color: $green-10;
		}

		&:disabled {
			cursor: default;
			opacity: 1;
		}
	}

	&#{$parent}--success#{$parent}--success-leaving {
		opacity: 0;
		pointer-events: none;
	}

	&__dot {
		flex-shrink: 0;
		width: 0.75rem;
		height: 0.75rem;
		border-radius: 50%;
		background-color: $brand-50;
		transform-origin: 50% 50%;
		animation: time-entry-actions-tracking-button-pulse 2s infinite linear;
	}

	&--modal-pause #{$parent}__dot {
		animation: none;
		opacity: 1;
	}

	&__icon-wrap {
		position: relative;
		flex-shrink: 0;
		width: 0.75rem;
		height: 0.75rem;
	}

	&__success-icon.sui-icon {
		position: absolute;
		top: 50%;
		left: 50%;
		fill: $green-60;
		transform: translate(-50%, -50%);
	}

	&__time {
		display: flex;
		min-width: 0;
		max-width: 100%;
		flex: 1 1 0;
		justify-content: center;
		align-items: center;
		gap: 0.25rem;
		overflow: hidden;
		color: $neutral-80;
		font-size: 1.5rem;
		line-height: 2rem;
		font-feature-settings: 'tnum';
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	&--success #{$parent}__time {
		color: $green-90;
	}
}

@include tracker-pulse-keyframes(time-entry-actions-tracking-button-pulse);
</style>
